import { Modal } from '@/components/common/dialog';
import { PoiLocationMarker } from '@/components/icons/floor-guide/PoiLocationMarker';
import useBaseSize from '@/hooks/useBaseSize';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore } from '@/stores/configStore';
import { getPoiById } from '@/utils/map-poi';
import { getHSize, getSize } from '@/utils/size';
import clsx from 'clsx';
import type { DabeeoMapData } from 'dabeeomaps';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    currentFloorName: string;
    handlePoiClick: (data: any) => void;
    mapData?: DabeeoMapData;
};

export function PoiListPopup({ isOpen, onClose, currentFloorName, handlePoiClick, mapData }: Props) {
    const { t } = useTranslation();
    const language = useConfigStore((state) => state.config.ui.language);
    const { baseWidth, baseHeight } = useBaseSize();
    const { isHorizontal } = useOrientation();
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const store = useConfigStore((state) => state.config.server?.store);
    const prevActiveElementRef = useRef<HTMLElement | null>(null);

    const handleMapData = (data: any) => {
        if (!Array.isArray(data)) {
            return [];
        }
        return data
            .filter((item) => item?.studioPoiId)
            .map((item) => ({ ...item, id: item.studioPoiId }));
    };

    const allPoiContents = useMemo(() => {
        return store?.buildings?.flatMap((building) => building?.poiContents ?? []) ?? [];
    }, [store]);

    const allFloorsOfStore = useMemo(() => {
        return store?.buildings?.flatMap((building) => building?.floors ?? []) ?? [];
    }, [store]);

    const currentPoiContent = useMemo(() => {
        if (!allPoiContents || !currentFloorName || !allFloorsOfStore) {
            return [];
        }

        const matchingFloorCodes = allFloorsOfStore?.filter((floor) => floor?.name?.[language] === currentFloorName)?.map((floor) => floor?.floor_code);

        const filteredPoiContents = allPoiContents.filter(
            (poi) => poi?.studioFloorId && matchingFloorCodes?.includes(poi.studioFloorId)
        );

        return handleMapData(filteredPoiContents);
    }, [allFloorsOfStore, currentFloorName, allPoiContents]);

    const handlePoiItemClick = (data: any, e: any) => {
        if (!data?.studioPoiId) {
            return;
        }
        const poiItem = getPoiById(mapData?.dataPoi?.getPois(), data.studioPoiId);
        if (!poiItem) {
            return;
        }
        handlePoiClick([poiItem]);
        const parent = e.currentTarget as HTMLElement;
        parent.classList.add('poi-active');
    };

    useEffect(() => {
        if (isOpen && !prevActiveElementRef.current) {
            prevActiveElementRef.current = document.activeElement as HTMLElement;
        }
    }, [isOpen]);

    const handleClose = () => {
        onClose();

        if (prevActiveElementRef.current) {
            const elementToRestore = prevActiveElementRef.current;
            prevActiveElementRef.current = null;

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (document.body.contains(elementToRestore) && elementToRestore.focus) {
                        try {
                            elementToRestore.focus({ preventScroll: false });
                        } catch (error) {
                            console.error(error);
                        }
                    }
                });
            });
        }
    };

    const closeRegionOrder = currentPoiContent.length > 0 ? 2 : 1;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            className={styles['poi-list__wrapper']}
            title={t('floor_guide.title.list_of_stores_and_facilities')}
            closeRegionOrder={closeRegionOrder}
        >
            <section
                className={clsx(
                    styles['poi-list'],
                    { [styles['poi-list__vertical--half-mode']]: !isHorizontal && screenMode === 'split' },
                    { [styles['poi-list__horizontal--half-mode']]: !!isHorizontal && screenMode === 'split' }
                )}
            >
                <div className={clsx(styles['poi-list__scrollbar'], 'scrollbar-custom-gray-99938f scrollbar-w-4')}>
                    {currentPoiContent.map((poi, index) => (
                        <div
                            key={`${poi.id}-${index}`}
                            className={clsx(styles['poi-list__item'], 'focus-border')}
                            data-modal-region={index === 0}
                            {...(index === 0 ? { 'data-modal-region-order': 1 } : {})}
                            data-interactive
                            data-playsound="ting"
                            aria-label={`${poi?.name?.[language] || poi?.name?.['ko'] || ''} ${t('common.data_role.popup_button')}`}
                            tabIndex={0}
                            onClick={(e) => handlePoiItemClick(poi, e)}
                            onKeyDown={(e) => e.key === 'Enter' && handlePoiItemClick(poi, e)}
                        >
                            <div className="size-[calc(36*var(--vw-unit))]">
                                <PoiLocationMarker width={getSize(36, baseWidth)} height={getHSize(36, baseHeight)} viewBox={`0 0 36 36`} />
                            </div>
                            <h3 className="line-clamp-1 text-lg font-semibold text-ellipsis">{poi?.name?.[language] || poi?.name?.['ko']}</h3>
                        </div>
                    ))}
                </div>
            </section>
        </Modal>
    );
}
